/**
 * requestQueue.ts
 *
 * Token-refresh queue for HTTP requests.
 *
 * When an API call returns 401 (expired access token):
 *   - The FIRST failing request triggers a token refresh.
 *   - ALL subsequent 401s that arrive while the refresh is in-flight are queued.
 *   - Once the refresh resolves, every queued request is retried automatically.
 *   - If the refresh fails (refresh token also expired), all queued requests
 *     are rejected and the session-end callback fires (→ redirect to login).
 */

import { NO_COGNITO_USER_ERROR, NO_REFRESH_TOKEN_ERROR } from '../auth/CognitoUser';

type RefreshCallback = () => Promise<void>;
type TokenGetter = () => string | null;
type SessionEndCallback = () => void;

// ─── Module-level auth callbacks ──────────────────────────────────────────────
// Registered once by AuthProvider via setupRequestAuth().
let _getToken: TokenGetter = () => null;
let _refresh: RefreshCallback | null = null;
let _onSessionEnd: SessionEndCallback | null = null;

/**
 * Cognito SDK error names that indicate the refresh token itself is no longer
 * usable. Only these should end the session — transient errors (network
 * failure, 5xx, timeout) must leave the user logged in so a subsequent attempt
 * can succeed.
 */
const AUTH_FAILURE_ERROR_NAMES = new Set([
  'NotAuthorizedException',
  'UserNotFoundException',
  'InvalidRefreshTokenException',
  'PasswordResetRequiredException',
  'UserNotConfirmedException',
]);

const AUTH_FAILURE_MESSAGE_PATTERNS = [
  /invalid refresh token/i,
  /refresh token.*(expired|revoked|invalid|unusable)/i,
  /cannot retrieve a new session/i,
  /please authenticate/i,
];

/**
 * Classify a refresh error: true → session is genuinely dead, false → transient
 * (network, timeout, server blip) and the user should stay logged in.
 */
const isAuthFailure = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false;
  const e = err as { name?: string; code?: string; __type?: string; message?: string };
  const identifiers = [e.name, e.code, e.__type]
    .filter((value): value is string => Boolean(value))
    .map((value) => value.split('#').pop()?.trim() ?? value);
  if (identifiers.some((value) => AUTH_FAILURE_ERROR_NAMES.has(value))) return true;
  // CognitoUser.refreshSession synthesises these when the session has no
  // refresh token to work with — treat as auth failure.
  if (e.message === NO_REFRESH_TOKEN_ERROR || e.message === NO_COGNITO_USER_ERROR) {
    return true;
  }
  const { message } = e;
  if (message && AUTH_FAILURE_MESSAGE_PATTERNS.some((pattern) => pattern.test(message))) {
    return true;
  }
  return false;
};

// ─── Queue state ──────────────────────────────────────────────────────────────
let isRefreshing = false;

interface QueuedItem {
  /** The original request to re-execute after a successful refresh. */
  execute: () => Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
}

const failedQueue: QueuedItem[] = [];

/** Flush all queued requests after a refresh attempt. */
const processQueue = (error: unknown): void => {
  const items = failedQueue.splice(0);
  for (const item of items) {
    if (error) {
      item.reject(error);
    } else {
      item.execute().then(item.resolve, item.reject);
    }
  }
};

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Register auth callbacks from AuthProvider.
 * Must be called once before any authenticated requests are made.
 */
export const setupRequestAuth = (
  getToken: TokenGetter,
  refresh: RefreshCallback,
  onSessionEnd: SessionEndCallback,
): void => {
  _getToken = getToken;
  _refresh = refresh;
  _onSessionEnd = onSessionEnd;
};

/**
 * Returns the current access token from the registered getter.
 * Used by HttpRequest to attach the Authorization header.
 */
export const getCurrentToken = (): string | null => _getToken();

/**
 * Called when a request receives a 401.
 *
 * - If a refresh is already in-flight, this request is queued and will
 *   automatically retry once the refresh resolves.
 * - Otherwise, a refresh is triggered. All queued requests drain after
 *   the refresh completes (success → retry all; failure → reject all).
 */
export const handleUnauthorized = <T>(execute: () => Promise<T>): Promise<T> => {
  // Refresh already in-flight — park this request in the queue
  if (isRefreshing) {
    return new Promise<T>((resolve, reject) => {
      failedQueue.push({
        execute: execute as () => Promise<unknown>,
        resolve: resolve as (value: unknown) => void,
        reject,
      });
    });
  }

  // We are the first 401 — own the refresh
  isRefreshing = true;

  return (async (): Promise<T> => {
    try {
      if (!_refresh) {
        throw new Error('[requestQueue] Auth not configured — call setupRequestAuth first.');
      }
      await _refresh();
      // Refresh succeeded: retry all queued requests then retry this one
      processQueue(null);
      return await execute();
    } catch (err) {
      // Refresh failed: queued requests can't be retried, so reject them.
      processQueue(err);
      // Only end the session when the refresh token is genuinely unusable.
      // Transient failures (network blip, 5xx) should leave the user logged in
      // so the next request can try again rather than bouncing them to /login.
      if (isAuthFailure(err)) {
        _onSessionEnd?.();
      }
      throw err;
    } finally {
      isRefreshing = false;
    }
  })();
};
